import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import type { ReplayEvent, ReplayEventType } from '../../../packetsReplay/extractReplayEvents'
import styles from './PlayerBottomBar.module.css'
import CollapseTab from './CollapseTab'
import { PlayIcon, PauseIcon, SkipBack10Icon, SkipForward10Icon, RestartIcon, OverviewIcon } from './PlayerIcons'
import { EVENT_ICON, EVENT_NAME } from './eventVisuals'
import { subscribeKey } from 'valtio/utils'
import { packetsReplayState } from '../../state/packetsReplayState'

interface Props {
  isPlaying: boolean
  progress: { current: number; total: number }
  speed: number
  totalTimeMs?: number
  currentTimeMs?: number
  packetTimesMs?: readonly number[]
  replayEvents?: ReplayEvent[]
  seekTo: number | null
  onPlayPause: (playing: boolean) => void
  onRestart: () => void
  onSpeedChange: (speed: number) => void
  onSeek: (index: number) => void
  onSeekToTime?: (timeMs: number) => void
  mobileLayout?: boolean
  /** True while a modal (e.g. settings) is open — the bar slides fully
   * below the viewport, leaving only its own collapse tab peeking. */
  modalOpen?: boolean
  /** Called when the peeking tab is tapped while modalOpen is true. */
  onCloseModal?: () => void
}

const formatTime = (ms: number) => {
  const s = Math.floor(ms / 1000)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

const SPEED_STEPS = [0.25, 0.5, 1, 2, 4, 8]

// Caption version shown under the controls in overview mode. RELEASE_TAG is
// defined by rsbuild (rsbuild.config.ts:135) from assets/release.json; when
// absent (dev) we call it 1.0 — the first complete release, per Тамерлан
// («у нас уже не 0.0.0 версия, считай это первая полноценная»).
const APP_VERSION_TAG = process.env.RELEASE_TAG ?? '1.0'

type DirtPieceStyle = CSSProperties & {
  '--dirt-x': string
  '--dirt-width': string
  '--dirt-height': string
  '--dirt-opacity': string
  '--dirt-blur': string
  '--dirt-skew': string
}

type ProgressGradientStyle = CSSProperties & {
  '--progress-blue-stop': string
  '--progress-teal-stop': string
  '--progress-gold-stop': string
  '--progress-red-stop': string
}

const buildProgressGradientStyle = (range: TimeRange, totalTimeMs: number): ProgressGradientStyle => {
  const duration = Math.max(1, range.end - range.start)
  const mapGlobalStop = (ratio: number) => `${(((ratio * totalTimeMs) - range.start) / duration) * 100}%`
  return {
    '--progress-blue-stop': mapGlobalStop(0),
    '--progress-teal-stop': mapGlobalStop(0.34),
    '--progress-gold-stop': mapGlobalStop(0.7),
    '--progress-red-stop': mapGlobalStop(1),
  }
}

let dirtSeed = 0x05_1a_7c_3d
const dirtRandom = () => {
  dirtSeed = (Math.imul(dirtSeed, 1_664_525) + 1_013_904_223) >>> 0
  return dirtSeed / 4_294_967_296
}

const DIRT_PIECES = Array.from({ length: 58 }, (_, index) => ({
  edge: dirtRandom() > 0.48 ? 'bottom' : 'top',
  style: {
    '--dirt-x': `${(dirtRandom() * 99.2).toFixed(2)}%`,
    '--dirt-width': `${1 + Math.floor(dirtRandom() * 13)}px`,
    '--dirt-height': `${3 + Math.floor(dirtRandom() * 27)}px`,
    '--dirt-opacity': (0.34 + dirtRandom() * 0.48).toFixed(2),
    '--dirt-blur': `${(dirtRandom() * 0.75).toFixed(2)}px`,
    '--dirt-skew': `${Math.round((dirtRandom() - 0.5) * 24)}deg`,
  } as DirtPieceStyle,
  key: index,
}))

const EVENT_CLASS: Partial<Record<ReplayEventType, string>> = {
  damage: styles.fight,
  death: styles.death,
  kill_player: styles.death,
  kill_mob: styles.fight,
  chat: styles.chat,
  command: styles.command,
  dimension_change: styles.dim,
  respawn: styles.dim,
  teleport: styles.dim,
  afk: styles.afk,
  afk_end: styles.dim,
  high_activity: styles.highActivity,
  low_activity: styles.lowActivity,
}

// ── Event clustering ────────────────────────────────────────────────────────
const EVENT_PRIORITY: ReplayEventType[] = [
  'death', 'kill_player', 'kill_mob', 'damage',
  'high_activity', 'dimension_change', 'teleport',
  'command', 'chat', 'afk', 'afk_end', 'respawn',
]

// Each icon stands on a pole; the pole's HEIGHT reads as the event's
// competitive weight — combat (death/kills/damage) reaches the tallest
// staff line, ambient/social events (chat, afk) barely leave the ground.
// A cluster uses its primaryType's tier (already the highest-priority type
// in the group, via EVENT_PRIORITY above), so a mixed cluster still shows
// its most significant member's height.
const EVENT_POLE_TIER: Partial<Record<ReplayEventType, 0 | 1 | 2 | 3>> = {
  death: 3, kill_player: 3, kill_mob: 3, damage: 3,
  high_activity: 2, dimension_change: 2, teleport: 2,
  command: 1, chat: 1,
  afk: 0, afk_end: 0, respawn: 0,
}
const POLE_HEIGHT_PX = [4, 6, 10, 14] as const

const CLUSTER_THRESHOLD_PCT = 2.8
// Minimum zoom-in window for the overview, derived from the replay length so
// short replays can still zoom into dense event clusters. A flat 1-min floor
// used to make a ~1-min replay effectively un-zoomable.
const MIN_VIEW_RANGE_FLOOR_MS = 5_000 // never tighter than ~5s — clusters stay separable & grabbable
const MIN_VIEW_RANGE_CAP_MS = 30_000 // never force more than 30s, even on long replays

type EventCluster = {
  events: ReplayEvent[]
  primaryType: ReplayEventType
  pct: number
}

type TimeRange = {
  start: number
  end: number
}

type RangeDrag = {
  mode: 'move' | 'start' | 'end'
  pointerId: number
  clientX: number
  initial: TimeRange
}

type MobileTimelineGesture = {
  pointerId: number
  startX: number
  startY: number
}

const clamp = (value: number, min: number, max: number) => (
  Math.max(min, Math.min(max, value))
)

const timeToPercent = (timeMs: number, range: TimeRange) => {
  const duration = Math.max(1, range.end - range.start)
  return ((timeMs - range.start) / duration) * 100
}

const getMinViewRange = (totalTimeMs: number) => (
  Math.min(
    totalTimeMs,
    // ~1/10 of the replay, but never below ~5s and never above 30s.
    clamp(totalTimeMs / 10, MIN_VIEW_RANGE_FLOOR_MS, MIN_VIEW_RANGE_CAP_MS)
  )
)

const normalizeRange = (
  start: number,
  end: number,
  totalTimeMs: number
): TimeRange => {
  if (totalTimeMs <= 0) return { start: 0, end: 0 }
  const minDuration = getMinViewRange(totalTimeMs)
  const duration = clamp(end - start, minDuration, totalTimeMs)
  const normalizedStart = clamp(start, 0, totalTimeMs - duration)
  return {
    start: normalizedStart,
    end: normalizedStart + duration,
  }
}

function clusterEvents (
  events: ReplayEvent[],
  range: TimeRange,
  thresholdPct = CLUSTER_THRESHOLD_PCT
): EventCluster[] {
  const duration = range.end - range.start
  if (!events.length || duration <= 0) return []
  const point = events
    .filter(event => (
      event.type !== 'low_activity' &&
      event.time >= range.start &&
      event.time <= range.end
    ))
    .sort((a, b) => a.time - b.time)
  const clusters: EventCluster[] = []
  let i = 0
  while (i < point.length) {
    const ev = point[i]!
    const pct = timeToPercent(ev.time, range)
    const group: ReplayEvent[] = [ev]
    let j = i + 1
    while (j < point.length) {
      const np = timeToPercent(point[j]!.time, range)
      if (np - pct <= thresholdPct) { group.push(point[j]!); j++ } else { break }
    }
    const primaryType = EVENT_PRIORITY.find(t => group.some(e => e.type === t)) ?? group[0]!.type
    clusters.push({ events: group, primaryType, pct })
    i = j
  }
  return clusters
}

type Segment = { left: number; width: number; type: 'fight' | 'chat' | 'active' }

// Thin exact-position stripes: one per point event, colored by type. They sit
// on top of the heat band so a precise moment is still findable inside a wide
// hot region.
function buildEventStripes (
  events: ReplayEvent[],
  range: TimeRange
): Segment[] {
  const segs: Segment[] = []
  const duration = range.end - range.start
  if (duration <= 0) return segs

  for (const ev of events) {
    if (ev.time < range.start || ev.time > range.end) continue
    const pct = timeToPercent(ev.time, range)
    const w = 0.4
    if (ev.type === 'death' || ev.type === 'damage' || ev.type === 'kill_player' || ev.type === 'kill_mob') {
      segs.push({ left: pct, width: w, type: 'fight' })
    } else if (ev.type === 'chat') {
      segs.push({ left: pct, width: w, type: 'chat' })
    } else {
      segs.push({ left: pct, width: w, type: 'active' })
    }
  }
  // Draw order = importance: danger stripes must never hide under gold/blue
  // ones sharing the same spot, so fight renders last (on top).
  const stripeWeight: Record<Segment['type'], number> = { chat: 0, active: 1, fight: 2 }
  segs.sort((a, b) => stripeWeight[a.type] - stripeWeight[b.type])
  return segs
}

// ── Activity heat band ──────────────────────────────────────────────────────
// The functional "затемнение": the track is read as a heat map of the
// recording. Each bucket is tinted with the color of its DOMINANT event kind
// (fight red / chat blue / command green / movement-activity gold — the same
// palette as the event icons and the info panel) at an opacity step
// proportional to event density. Dark stretches literally mean "nothing
// happened here"; saturated stretches mean "life". No legend needed — the
// colors are already taught by the icons row above.
type HeatKind = 'fight' | 'chat' | 'command' | 'active'
type HeatBand = {
  left: number
  width: number
  kind: HeatKind
  level: 1 | 2 | 3
  /** Present when the bucket mixes kinds: rendered as an under-stripe so a
   *  red+gold moment doesn't dissolve into "just another gold band". */
  secondary?: HeatKind
}

const HEAT_BUCKETS = 72
// Priority when a bucket mixes kinds: danger outranks movement outranks
// intent (commands) outranks talk — same ordering the cluster icons use.
const HEAT_KIND_PRIORITY: HeatKind[] = ['fight', 'active', 'command', 'chat']

const heatKindOf = (type: ReplayEventType): HeatKind | null => {
  if (type === 'death' || type === 'damage' || type === 'kill_player' || type === 'kill_mob') return 'fight'
  if (type === 'chat') return 'chat'
  if (type === 'command') return 'command'
  if (type === 'low_activity') return null // its own segment row already shows it
  return 'active'
}

function buildHeatBands (events: ReplayEvent[], range: TimeRange): HeatBand[] {
  const duration = range.end - range.start
  if (!events.length || duration <= 0) return []

  const counts = Array.from({ length: HEAT_BUCKETS }, () => 0)
  const kindCounts = Array.from({ length: HEAT_BUCKETS }, () => new Map<HeatKind, number>())

  for (const ev of events) {
    if (ev.time < range.start || ev.time > range.end) continue
    const kind = heatKindOf(ev.type)
    if (!kind) continue
    const bucket = Math.min(
      HEAT_BUCKETS - 1,
      Math.floor(((ev.time - range.start) / duration) * HEAT_BUCKETS)
    )
    counts[bucket]!++
    const perKind = kindCounts[bucket]!
    perKind.set(kind, (perKind.get(kind) ?? 0) + 1)
  }

  const maxCount = Math.max(1, ...counts)
  const bands: HeatBand[] = []
  for (let i = 0; i < HEAT_BUCKETS; i++) {
    if (counts[i] === 0) continue
    const perKind = kindCounts[i]!
    let kind: HeatKind = 'active'
    let best = -1
    for (const candidate of HEAT_KIND_PRIORITY) {
      const count = perKind.get(candidate) ?? 0
      if (count > best) {
        best = count
        kind = candidate
      }
    }
    // Second voice of a mixed bucket: the strongest remaining kind.
    let secondary: HeatKind | undefined
    let secondBest = 0
    for (const candidate of HEAT_KIND_PRIORITY) {
      if (candidate === kind) continue
      const count = perKind.get(candidate) ?? 0
      if (count > secondBest) {
        secondBest = count
        secondary = candidate
      }
    }
    const ratio = counts[i]! / maxCount
    const level: HeatBand['level'] = ratio > 0.66 ? 3 : ratio > 0.33 ? 2 : 1
    const left = (i / HEAT_BUCKETS) * 100
    const width = 100 / HEAT_BUCKETS
    // Merge with the previous band when contiguous and identical — fewer nodes.
    const previous = bands.at(-1)
    if (previous && previous.kind === kind && previous.level === level &&
      previous.secondary === secondary &&
      Math.abs(previous.left + previous.width - left) < 0.01) {
      previous.width += width
    } else {
      bands.push({ left, width, kind, level, secondary })
    }
  }
  return bands
}

function buildTicks (range: TimeRange) {
  const duration = range.end - range.start
  if (duration <= 0) return []
  const major =
    duration < 60_000 ? 10_000
      : duration < 120_000 ? 15_000
        : duration < 300_000 ? 30_000
          : duration < 900_000 ? 60_000
            : duration < 1_800_000 ? 120_000
              : duration < 3_600_000 ? 300_000
                : 600_000
  // A proper instrument ruler: three grades of graduation. Minor every
  // major/10, a taller mid mark at every half-major, labels on majors.
  const minor = major / 10
  const result: Array<{ ms: number; pct: number; label: string | null; isMajor: boolean; isMid: boolean }> = []
  const first = Math.ceil(range.start / minor) * minor

  for (let time = first; time <= range.end; time += minor) {
    const majorRatio = time / major
    const isMajor = Math.abs(majorRatio - Math.round(majorRatio)) < 0.001
    const midRatio = time / (major / 2)
    const isMid = !isMajor && Math.abs(midRatio - Math.round(midRatio)) < 0.001
    result.push({
      ms: time,
      pct: timeToPercent(time, range),
      label: isMajor ? formatTime(time) : null,
      isMajor,
      isMid,
    })
  }
  return result
}

export default function PlayerBottomBar ({
  isPlaying, progress, speed, totalTimeMs, currentTimeMs, packetTimesMs, replayEvents, seekTo,
  onPlayPause, onRestart, onSpeedChange, onSeek, onSeekToTime, mobileLayout = false,
  modalOpen = false, onCloseModal
}: Props) {
  const displayCurrent = typeof seekTo === 'number' ? seekTo : progress.current
  const getTimeForIndex = useCallback((index: number) => {
    if (!totalTimeMs || progress.total <= 0) return 0
    const fallback = (index / progress.total) * totalTimeMs
    if (!packetTimesMs?.length) return fallback
    const clamped = Math.max(0, Math.min(packetTimesMs.length - 1, Math.round(index)))
    return packetTimesMs[clamped] ?? fallback
  }, [packetTimesMs, progress.total, totalTimeMs])
  const currentMs = totalTimeMs && progress.total > 0
    ? (typeof seekTo !== 'number' && typeof currentTimeMs === 'number' ? currentTimeMs : getTimeForIndex(displayCurrent)) : 0
  const [viewRange, setViewRange] = useState<TimeRange>({ start: 0, end: 0 })
  const [overviewOpen, setOverviewOpen] = useState(false)
  const overviewRef = useRef<HTMLDivElement>(null)
  const barRef = useRef<HTMLDivElement>(null)
  const rulerRef = useRef<HTMLDivElement>(null)
  const rangeDragRef = useRef<RangeDrag>()
  const rulerDraggingRef = useRef<{ pointerId: number } | undefined>()
  const mobileTimelineGestureRef = useRef<MobileTimelineGesture>()
  const suppressMobileHandleClickRef = useRef(false)
  const previousTotalTimeRef = useRef(0)
  const fullRange = useMemo<TimeRange>(() => ({
    start: 0,
    end: totalTimeMs ?? 0,
  }), [totalTimeMs])
  const effectiveRange = viewRange.end > viewRange.start
    ? viewRange
    : fullRange
  const visibleDuration = Math.max(0, effectiveRange.end - effectiveRange.start)
  const minViewRange = totalTimeMs ? getMinViewRange(totalTimeMs) : 0
  const currentInView = currentMs >= effectiveRange.start &&
    currentMs <= effectiveRange.end
  const headPct = totalTimeMs
    ? timeToPercent(currentMs, effectiveRange)
    : (progress.total > 0 ? (displayCurrent / progress.total) * 100 : 0)
  const fullHeadPct = totalTimeMs
    ? (currentMs / totalTimeMs) * 100
    : headPct
  const primaryProgressGradient = totalTimeMs
    ? buildProgressGradientStyle(effectiveRange, totalTimeMs)
    : undefined
  const seekToMs = useCallback((timeMs: number) => {
    if (onSeekToTime) {
      onSeekToTime(timeMs)
    } else if (totalTimeMs) {
      onSeek(Math.round((timeMs / totalTimeMs) * progress.total))
    }
  }, [onSeek, onSeekToTime, progress.total, totalTimeMs])

  // Shared ratio→time seek used by both tap and drag on the ruler. Pointer
  // events (not onClick/onMouseMove) so a finger drag scrubs the timeline on
  // mobile — mouse/pen drag still works too, since pointer events subsume
  // them. The third gesture promised in MobileReplayMenu's GESTURES list.
  const seekToRulerX = useCallback((clientX: number) => {
    const rect = rulerRef.current?.getBoundingClientRect()
    if (!rect) return
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    if (totalTimeMs) {
      seekToMs(
        effectiveRange.start +
        ratio * (effectiveRange.end - effectiveRange.start)
      )
    } else {
      onSeek(Math.round(ratio * progress.total))
    }
  }, [effectiveRange, progress.total, onSeek, seekToMs, totalTimeMs])

  const handleRulerPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    e.stopPropagation()
    seekToRulerX(e.clientX)
    rulerDraggingRef.current = { pointerId: e.pointerId }
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      // Document-level pointerup still clears the drag if capture is rejected.
    }
  }, [seekToRulerX])

  const handleRulerPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (rulerDraggingRef.current?.pointerId !== e.pointerId) return
    seekToRulerX(e.clientX)
  }, [seekToRulerX])

  const handleRulerPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (rulerDraggingRef.current?.pointerId !== e.pointerId) return
    rulerDraggingRef.current = undefined
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId)
      } catch {}
    }
  }, [])

  const cycleSpeed = () => {
    const idx = SPEED_STEPS.indexOf(speed)
    onSpeedChange(SPEED_STEPS[(idx + 1) % SPEED_STEPS.length] ?? 1)
  }

  const skip = (deltaMs: number) => {
    if (!totalTimeMs || progress.total <= 0) return
    const newMs = Math.max(0, Math.min(totalTimeMs, currentMs + deltaMs))
    seekToMs(newMs)
  }

  const stripes = totalTimeMs && replayEvents
    ? buildEventStripes(replayEvents, effectiveRange)
    : []
  const heatBands = useMemo(
    () => ((replayEvents && totalTimeMs)
      ? buildHeatBands(replayEvents, effectiveRange)
      : []),
    [effectiveRange, replayEvents, totalTimeMs]
  )
  const overviewHeatBands = useMemo(
    () => ((replayEvents && totalTimeMs)
      ? buildHeatBands(replayEvents, fullRange)
      : []),
    [fullRange, replayEvents, totalTimeMs]
  )

  const clusters = useMemo(
    () => ((replayEvents && totalTimeMs)
      ? clusterEvents(replayEvents, effectiveRange)
      : []),
    [effectiveRange, replayEvents, totalTimeMs]
  )
  const overviewClusters = useMemo(
    () => ((replayEvents && totalTimeMs)
      ? clusterEvents(replayEvents, fullRange, 1.6)
      : []),
    [fullRange, replayEvents, totalTimeMs]
  )

  const ticks = useMemo(() => buildTicks(effectiveRange), [effectiveRange])

  const hasEvents = Boolean(replayEvents?.length && totalTimeMs && progress.total > 0)

  const [collapsed, setCollapsed] = useState(packetsReplayState.bottomBarCollapsed)
  // Let external toggles (TopMenu "hide UI", via packetsReplayState.bottomBarCollapsed)
  // minimize the timeline — the collapse tab still writes the same flag.
  useEffect(() => subscribeKey(packetsReplayState, 'bottomBarCollapsed', (v) => setCollapsed(v)), [])
  const [eventPopup, setEventPopup] = useState<{ ci: number; x: number; y: number } | null>(null)
  // Second-level card: hovering a row of the cluster list opens the full
  // detail card beside the list (map-service popup pattern).
  const [nestedEvent, setNestedEvent] = useState<{ ev: ReplayEvent; top: number } | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const total = totalTimeMs ?? 0
    if (total === previousTotalTimeRef.current) return
    previousTotalTimeRef.current = total
    setViewRange({ start: 0, end: total })
  }, [totalTimeMs])

  useEffect(() => {
    // The replay controls and vanilla HUD live inside the scaled #ui-root,
    // while the canvas hotbar is portalled to document.body. Keep one local
    // reservation for HUD bars and convert it to screen space for the hotbar.
    // While a modal is open, the bar itself is off-screen — only its collapse
    // tab peeks up, so other HUD elements only need to clear that sliver.
    const panelHeight = modalOpen
      ? 20
      : mobileLayout
        ? (overviewOpen ? 150 : 106)
        : collapsed ? 34 : overviewOpen ? 96 : 44
    const localBottom = `calc(${panelHeight}px * var(--ui-scale, 1) + 6px)`
    const screenBottom = `calc((${panelHeight}px * var(--ui-scale, 1) + 6px) * var(--guiScale, 1))`
    const root = document.documentElement
    root.style.setProperty('--hud-bottom-max', screenBottom)
    root.style.setProperty('--safe-area-inset-bottom', localBottom)
    return () => {
      root.style.removeProperty('--hud-bottom-max')
      root.style.removeProperty('--safe-area-inset-bottom')
    }
  }, [collapsed, mobileLayout, overviewOpen, modalOpen])

  const updateViewRange = useCallback((start: number, end: number) => {
    if (!totalTimeMs) return
    setViewRange(normalizeRange(start, end, totalTimeMs))
    setEventPopup(null)
  }, [totalTimeMs])

  const applyRangeDrag = useCallback((pointerId: number, clientX: number) => {
    const drag = rangeDragRef.current
    const bounds = overviewRef.current?.getBoundingClientRect()
    if (!drag || !bounds || !totalTimeMs || drag.pointerId !== pointerId) return
    const deltaMs = ((clientX - drag.clientX) / bounds.width) * totalTimeMs
    const duration = drag.initial.end - drag.initial.start

    if (drag.mode === 'move') {
      updateViewRange(
        drag.initial.start + deltaMs,
        drag.initial.start + deltaMs + duration
      )
    } else if (drag.mode === 'start') {
      const minDuration = getMinViewRange(totalTimeMs)
      updateViewRange(
        clamp(
          drag.initial.start + deltaMs,
          0,
          drag.initial.end - minDuration
        ),
        drag.initial.end
      )
    } else {
      const minDuration = getMinViewRange(totalTimeMs)
      updateViewRange(
        drag.initial.start,
        clamp(
          drag.initial.end + deltaMs,
          drag.initial.start + minDuration,
          totalTimeMs
        )
      )
    }
  }, [totalTimeMs, updateViewRange])

  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      applyRangeDrag(event.pointerId, event.clientX)
    }
    const onPointerEnd = (event: PointerEvent) => {
      if (rangeDragRef.current?.pointerId !== event.pointerId) return
      rangeDragRef.current = undefined
      if (overviewRef.current?.hasPointerCapture(event.pointerId)) {
        overviewRef.current.releasePointerCapture(event.pointerId)
      }
    }
    document.addEventListener('pointermove', onPointerMove)
    document.addEventListener('pointerup', onPointerEnd)
    document.addEventListener('pointercancel', onPointerEnd)
    return () => {
      document.removeEventListener('pointermove', onPointerMove)
      document.removeEventListener('pointerup', onPointerEnd)
      document.removeEventListener('pointercancel', onPointerEnd)
    }
  }, [applyRangeDrag])

  const startRangeDrag = (
    mode: RangeDrag['mode'],
    event: React.PointerEvent
  ) => {
    if (!totalTimeMs || event.button !== 0) return
    event.preventDefault()
    event.stopPropagation()
    rangeDragRef.current = {
      mode,
      pointerId: event.pointerId,
      clientX: event.clientX,
      initial: effectiveRange,
    }
    try {
      overviewRef.current?.setPointerCapture(event.pointerId)
    } catch {
      // Document-level pointer handlers keep dragging reliable if capture is rejected.
    }
  }

  const handleOverviewPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    applyRangeDrag(event.pointerId, event.clientX)
  }

  const finishRangeDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (rangeDragRef.current?.pointerId !== event.pointerId) return
    rangeDragRef.current = undefined
    if (overviewRef.current?.hasPointerCapture(event.pointerId)) {
      overviewRef.current.releasePointerCapture(event.pointerId)
    }
  }

  const handleOverviewPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!totalTimeMs || event.button !== 0) return
    const bounds = event.currentTarget.getBoundingClientRect()
    const ratio = clamp((event.clientX - bounds.left) / bounds.width, 0, 1)
    const duration = effectiveRange.end - effectiveRange.start
    const center = ratio * totalTimeMs
    updateViewRange(center - duration / 2, center + duration / 2)
  }

  const handleOverviewWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (!totalTimeMs) return
    event.preventDefault()
    event.stopPropagation()
    const bounds = event.currentTarget.getBoundingClientRect()
    const pointerRatio = clamp((event.clientX - bounds.left) / bounds.width, 0, 1)
    const pointerTime = pointerRatio * totalTimeMs
    const duration = effectiveRange.end - effectiveRange.start
    const anchorRatio = clamp(
      (pointerTime - effectiveRange.start) / Math.max(1, duration),
      0,
      1
    )
    const minDuration = getMinViewRange(totalTimeMs)
    const wheelDelta = event.deltaY || event.deltaX
    const zoomFactor = Math.exp(
      Math.sign(wheelDelta) * Math.min(0.32, Math.abs(wheelDelta) * 0.003)
    )
    const nextDuration = clamp(
      duration * zoomFactor,
      minDuration,
      totalTimeMs
    )
    const nextStart = pointerTime - anchorRatio * nextDuration
    updateViewRange(nextStart, nextStart + nextDuration)
  }

  const zoomView = (factor: number) => {
    if (!totalTimeMs) return
    const duration = effectiveRange.end - effectiveRange.start
    if (duration <= 0) return
    const center = currentInView
      ? currentMs
      : effectiveRange.start + duration / 2
    const nextDuration = clamp(duration * factor, getMinViewRange(totalTimeMs), totalTimeMs)
    updateViewRange(center - nextDuration / 2, center + nextDuration / 2)
  }

  // ── Wheel = panel state: collapsed ⇄ normal ⇄ overview ────────────────────
  // Scrolling up over the bar opens it further, scrolling down closes it, one
  // step per gesture. The overview track keeps its own wheel (range zoom), so
  // gestures that start there never reach this handler.
  const wheelSwitchAt = useRef(0)
  const setCollapsedState = (next: boolean) => {
    setCollapsed(next)
    packetsReplayState.bottomBarCollapsed = next
  }
  useEffect(() => {
    if (!mobileLayout) return
    setCollapsedState(false)
    setOverviewOpen(false)
    setEventPopup(null)
    setNestedEvent(null)
  }, [mobileLayout])

  const startMobileTimelineGesture = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return
    event.stopPropagation()
    mobileTimelineGestureRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
    }
    try {
      event.currentTarget.setPointerCapture(event.pointerId)
    } catch {}
  }

  const finishMobileTimelineGesture = (event: React.PointerEvent<HTMLButtonElement>) => {
    const gesture = mobileTimelineGestureRef.current
    if (!gesture || gesture.pointerId !== event.pointerId) return
    mobileTimelineGestureRef.current = undefined
    const deltaX = event.clientX - gesture.startX
    const deltaY = event.clientY - gesture.startY
    if (Math.abs(deltaY) < 24 || Math.abs(deltaY) < Math.abs(deltaX) * 1.15) return
    suppressMobileHandleClickRef.current = true
    setCollapsedState(false)
    setOverviewOpen(deltaY < 0)
    window.setTimeout(() => {
      suppressMobileHandleClickRef.current = false
    }, 0)
  }
  // Native capture-phase listener: the in-game wheel handlers (hotbar slot
  // scroll) run on the document and never let a React bubble handler on the
  // bar see the event. Capturing on the document wins the race; gestures
  // that start inside the overview track still fall through to its zoom.
  useEffect(() => {
    const onWheel = (event: WheelEvent) => {
      if (!barRef.current?.contains(event.target as Node)) return
      if (overviewRef.current?.contains(event.target as Node)) return
      const delta = event.deltaY
      if (Math.abs(delta) < 4) return
      event.preventDefault()
      event.stopPropagation()
      const now = performance.now()
      if (now - wheelSwitchAt.current < 280) return
      wheelSwitchAt.current = now
      if (delta < 0) {
        // expand: collapsed → normal → overview
        if (collapsed) setCollapsedState(false)
        else if (!overviewOpen) setOverviewOpen(true)
      } else if (overviewOpen) {
        // shrink: overview → normal → collapsed
        setOverviewOpen(false)
      } else if (!collapsed) {
        setCollapsedState(true)
      }
    }
    document.addEventListener('wheel', onWheel, { capture: true, passive: false })
    return () => {
      document.removeEventListener('wheel', onWheel, { capture: true })
    }
  }, [collapsed, overviewOpen])

  const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null } }
  const scheduleClose = () => {
    cancelClose()
    closeTimer.current = setTimeout(() => {
      setEventPopup(null)
      setNestedEvent(null)
    }, 220)
  }
  useEffect(() => () => cancelClose(), [])
  const openClusterPopup = (ci: number, target: HTMLElement) => {
    cancelClose()
    const r = target.getBoundingClientRect()
    setNestedEvent(null)
    setEventPopup({ ci, x: r.left + r.width / 2, y: r.top })
  }

  return (
    <div
      className={[
        styles.bottomBar,
        mobileLayout ? styles.mobileLayout : '',
        collapsed ? styles.collapsed : '',
        !collapsed && overviewOpen ? styles.overviewOpen : '',
        modalOpen ? styles.hiddenByModal : '',
      ].filter(Boolean).join(' ')}
      ref={barRef}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      data-replay-ui
      data-replay-bottom-bar
    >
      {/* Collapse tab — the one visible way OUT of the collapsed strip
         (wheel gestures are invisible affordances). Sits above the right
         cluster, beside the speed plate. While a modal is open the whole
         bar slides below the viewport (see .hiddenByModal) and this same
         tab is the only thing still peeking — tapping it closes the modal
         instead of toggling the timeline collapse. */}
      <CollapseTab
        edge="top"
        collapsed={modalOpen || collapsed}
        onToggle={() => { modalOpen ? onCloseModal?.() : setCollapsedState(!collapsed) }}
        title={modalOpen ? 'Скрыть настройки' : collapsed ? 'Развернуть таймлайн' : 'Свернуть таймлайн'}
        className={styles.bottomCollapseTab}
      />

      <button
        type="button"
        className={styles.mobileTimelineHandle}
        title={modalOpen ? 'Вернуть блок управления' : overviewOpen ? 'Скрыть подробный таймлайн' : 'Показать подробный таймлайн'}
        aria-label={modalOpen ? 'Вернуть блок управления' : overviewOpen ? 'Скрыть подробный таймлайн' : 'Показать подробный таймлайн'}
        aria-expanded={overviewOpen}
        onPointerDown={modalOpen ? undefined : startMobileTimelineGesture}
        onPointerUp={modalOpen ? undefined : finishMobileTimelineGesture}
        onPointerCancel={() => { mobileTimelineGestureRef.current = undefined }}
        onClick={() => {
          if (modalOpen) {
            onCloseModal?.()
            return
          }
          if (suppressMobileHandleClickRef.current) return
          setCollapsedState(false)
          setOverviewOpen(open => !open)
        }}
      >
        <span aria-hidden="true" />
      </button>

      <span className={styles.hangingHistogram} aria-hidden="true">
        {DIRT_PIECES.map(piece => (
          <i
            key={piece.key}
            className={styles.hangingHistogramPiece}
            data-edge={piece.edge}
            style={piece.style}
          />
        ))}
      </span>

      {/* Controls — order: [Restart] [SkipBack10] [Play] [SkipForward10] [Time].
         Play sits in the geometric center of the cluster, flanked symmetrically
         by the ±10s seek buttons. Restart (jump to very start) is the
         tertiary action on the far left. */}
      <div className={styles.controls}>
        <button className={styles.restartBtn} onClick={onRestart} title="В начало">
          <span className={styles.restartIconWrap}>
            <RestartIcon size="100%" />
          </span>
        </button>
        <button className={styles.skipBtn} onClick={() => skip(-10_000)} title="-10с">
          <span className={styles.skipIconWrap}>
            <SkipBack10Icon size="100%" />
          </span>
        </button>
        <button
          className={styles.playBtn}
          data-playing={isPlaying ? 'true' : 'false'}
          onClick={() => onPlayPause(!isPlaying)}
          title={isPlaying ? 'Пауза' : 'Играть'}
        >
          <span className={styles.playIcon}>
            {isPlaying ? <PauseIcon size="100%" /> : <PlayIcon size="100%" />}
          </span>
        </button>
        <button className={styles.skipBtn} onClick={() => skip(10_000)} title="+10с">
          <span className={styles.skipIconWrap}>
            <SkipForward10Icon size="100%" />
          </span>
        </button>
        <span className={styles.timeWrap}>
          <span className={styles.timeDisplay} data-playing={isPlaying ? 'true' : 'false'}>
            <span className={styles.timeSweep} aria-hidden="true" />
            {totalTimeMs ? (
              <>
                <span className={styles.timeCurrent}>{formatTime(currentMs)}</span>
                <span className={styles.timeSep}>/</span>
                <span className={styles.timeTotal}>{formatTime(totalTimeMs)}</span>
              </>
            ) : String(displayCurrent)}
          </span>
        </span>
        {/* Version caption — one quiet engraved line under the transport
           buttons (overview mode only): single style, tight, sunk into a
           soft dark backdrop so it reads as a stamp, not a UI element. */}
        {!collapsed && overviewOpen && (
          <span className={styles.versionCaption} aria-hidden="true">
            minecraft-replay-engine&nbsp;v{APP_VERSION_TAG}
          </span>
        )}
      </div>
      {!collapsed && <div className={styles.timelineWrap}>
        <div className={styles.primaryTimeline}>

          {/* Event icon boxes */}
          {hasEvents && (
            <div className={styles.eventsRow}>
              {/* Played tint: the same three staff lines re-drawn in blue and
                 clipped to the playhead — blue staff = already watched. */}
              <div
                className={styles.eventsRowPlayed}
                style={{ clipPath: `inset(0 ${100 - clamp(headPct, 0, 100)}% 0 0)` }}
                aria-hidden="true"
              />
              {/* Low-activity segments */}
              {replayEvents!.filter(ev => (
                ev.type === 'low_activity' &&
                ev.time + (ev.duration ?? 0) >= effectiveRange.start &&
                ev.time <= effectiveRange.end
              )).map((ev, i) => {
                const start = Math.max(ev.time, effectiveRange.start)
                const end = Math.min(
                  ev.time + (ev.duration ?? 0),
                  effectiveRange.end
                )
                const pct = timeToPercent(start, effectiveRange)
                const widthPct = timeToPercent(end, effectiveRange) - pct
                return (
                  <div
                    key={`seg-${i}`}
                    className={`${styles.eventIcon} ${styles.lowActivity} ${styles.segment}`}
                    style={{ left: `${pct}%`, width: `${widthPct}%`, transform: 'none' }}
                    title={`${ev.label} (${Math.round((ev.duration ?? 0) / 1000)}с)`}
                    onClick={() => seekToMs(ev.time)}
                  />
                )
              })}

              {/* Clustered point events */}
              {clusters.map((cluster, ci) => {
                const isMulti = cluster.events.length > 1
                // A run of same-type single events (chat spam, footstep pings…)
                // keeps only its first icon at full size; the followers shrink
                // so the run reads as one phrase, not a picket fence.
                const previous = clusters[ci - 1]
                const isRepeat = !isMulti && previous && previous.events.length === 1 &&
                  previous.primaryType === cluster.primaryType &&
                  (cluster.pct - previous.pct) < 6
                const iconCls = [
                  styles.eventIcon,
                  EVENT_CLASS[cluster.primaryType] ?? '',
                  isRepeat ? styles.eventIconRepeat : '',
                ].filter(Boolean).join(' ')
                const poleTier = EVENT_POLE_TIER[cluster.primaryType] ?? 1
                const poleHeight = POLE_HEIGHT_PX[poleTier]
                return (
                  <div
                    key={`cl-${ci}`}
                    className={isMulti
                      ? `${styles.eventIcon} ${styles.clusterMarker}`
                      : iconCls}
                    style={{ left: `${cluster.pct}%`, '--pole-h': `${poleHeight}px` } as React.CSSProperties}
                    aria-label={isMulti ? `${cluster.events.length} событий` : (cluster.events[0]?.label ?? cluster.primaryType)}
                    onClick={(e) => {
                      if (isMulti) {
                        e.stopPropagation()
                        openClusterPopup(ci, e.currentTarget)
                      } else {
                        seekToMs(cluster.events[0]!.time)
                      }
                    }}
                    onMouseEnter={(e) => {
                      openClusterPopup(ci, e.currentTarget)
                    }}
                    onMouseLeave={scheduleClose}
                  >
                    {isMulti
                      ? <span className={styles.clusterCount}>{cluster.events.length}</span>
                      : (EVENT_ICON[cluster.primaryType]
                        ? <span className={`pixelart-icons-font-${EVENT_ICON[cluster.primaryType]}`} />
                        : '●'
                      )}
                  </div>
                )
              })}

              {/* Event popup — map-service style: an anchored card with a
                 diamond tail. One event = a detail card; several events = a
                 header + list, and hovering a row opens the detail card as a
                 second-level popup beside the list. */}
              {eventPopup && (() => {
                const popupEvents = clusters[eventPopup.ci]?.events ?? []
                if (!popupEvents.length) return null
                const single = popupEvents.length === 1 ? popupEvents[0]! : null
                const renderDetail = (ev: ReplayEvent, className: string, style?: React.CSSProperties) => (
                  <div className={`${className} ${EVENT_CLASS[ev.type] ?? ''}`.trim()} style={style}>
                    <div className={styles.popupDetailHead}>
                      <span className={`${styles.popupDetailIcon} ${EVENT_CLASS[ev.type] ?? ''}`}>
                        {EVENT_ICON[ev.type]
                          ? <span className={`pixelart-icons-font-${EVENT_ICON[ev.type]}`} />
                          : '●'}
                      </span>
                      <span className={styles.popupDetailType}>{EVENT_NAME[ev.type] ?? ev.type}</span>
                      <span className={styles.popupDetailTime}>{formatTime(ev.time)}</span>
                    </div>
                    {ev.label && ev.label !== (EVENT_NAME[ev.type] ?? '') && (
                      <div className={styles.popupDetailLabel}>{ev.label}</div>
                    )}
                    <div className={styles.popupDetailHint}>клик — перейти к моменту</div>
                  </div>
                )
                return createPortal(
                  <div
                    className={styles.clusterPopup}
                    style={{ left: eventPopup.x, top: eventPopup.y }}
                    onMouseEnter={cancelClose}
                    onMouseLeave={scheduleClose}
                  >
                    {single ? (
                      <div
                        className={styles.popupSingle}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation()
                          setEventPopup(null)
                          seekToMs(single.time)
                        }}
                      >
                        {renderDetail(single, styles.popupDetail)}
                      </div>
                    ) : (
                      <>
                        <div className={styles.popupHeader}>
                          <span className={styles.popupDia} aria-hidden="true" />
                          <span className={styles.popupTitle}>{popupEvents.length} событий</span>
                          <span className={styles.popupRange}>
                            {formatTime(popupEvents[0]!.time)}–{formatTime(popupEvents.at(-1)!.time)}
                          </span>
                        </div>
                        <div className={styles.popupList}>
                          {popupEvents.map((ev, ei) => (
                            <button
                              key={`${ev.time}-${ev.type}-${ei}`}
                              type="button"
                              className={`${styles.clusterPopupItem} ${EVENT_CLASS[ev.type] ?? ''}`}
                              onClick={(e) => {
                                e.stopPropagation()
                                setEventPopup(null)
                                setNestedEvent(null)
                                seekToMs(ev.time)
                              }}
                              onMouseEnter={(e) => {
                                setNestedEvent({ ev, top: e.currentTarget.offsetTop })
                              }}
                            >
                              <span className={styles.clusterPopupIcon}>
                                {EVENT_ICON[ev.type]
                                  ? <span className={`pixelart-icons-font-${EVENT_ICON[ev.type]}`} />
                                  : '●'}
                              </span>
                              <span className={styles.clusterPopupTime}>{formatTime(ev.time)}</span>
                              <span className={styles.clusterPopupLabel}>{ev.label ?? EVENT_NAME[ev.type] ?? ev.type}</span>
                            </button>
                          ))}
                        </div>
                        {nestedEvent && renderDetail(
                          nestedEvent.ev,
                          `${styles.popupDetail} ${styles.popupNested}`,
                          { top: nestedEvent.top }
                        )}
                      </>
                    )}
                    <span className={styles.popupTail} aria-hidden="true" />
                  </div>,
                  document.body
                )
              })()}
            </div>
          )}
          {!hasEvents && <div className={styles.emptyEventsRow} />}

          {/* Ruler: activity heat band (density × dominant event kind), exact
             event stripes on top, then the unplayed side dimmed so played =
             bright, ahead = dark. Progress doubles as a gold thread along the
             bottom edge, so it stays readable over empty stretches too. */}
          <div
            ref={rulerRef}
            className={styles.ruler}
            onPointerDown={handleRulerPointerDown}
            onPointerMove={handleRulerPointerMove}
            onPointerUp={handleRulerPointerUp}
            onPointerCancel={handleRulerPointerUp}
          >
            {heatBands.map((band, i) => (
              <div
                key={`h${i}`}
                className={`${styles.heat} ${styles[`heat_${band.kind}`]} ${styles[`heatL${band.level}`]}`}
                style={{ left: `${band.left}%`, width: `${band.width}%` }}
              >
                {band.secondary && (
                  <span className={`${styles.heatSub} ${styles[`heat_${band.secondary}`]}`} />
                )}
              </div>
            ))}
            {totalTimeMs ? (
              <div
                className={styles.playedFill}
                style={{ width: `${clamp(headPct, 0, 100)}%` }}
              />
            ) : null}
            {stripes.map((seg, i) => (
              <div
                key={i}
                className={`${styles.rulerSegment} ${styles[seg.type]}`}
                style={{ left: `${seg.left}%`, width: `${seg.width}%` }}
              />
            ))}
            {totalTimeMs ? (
              <>
                <div
                  className={styles.unplayedShade}
                  style={{ left: `${clamp(headPct, 0, 100)}%` }}
                />
                <div
                  className={styles.progressThread}
                  style={{
                    ...primaryProgressGradient,
                    clipPath: `inset(0 ${100 - clamp(headPct, 0, 100)}% 0 0)`,
                  }}
                />
              </>
            ) : null}
            {currentInView && (
              <div className={styles.playhead} style={{ left: `${headPct}%` }}>
                <span className={styles.playheadTime}>
                  {totalTimeMs ? formatTime(currentMs) : String(displayCurrent)}
                </span>
              </div>
            )}

            <div className={styles.tickRuler} aria-hidden="true">
              {ticks.map(tick => (
                <div
                  key={tick.ms}
                  className={[
                    styles.tick,
                    tick.isMajor ? '' : tick.isMid ? styles.tickMid : styles.tickMinor,
                    // Edge labels would centre on the rail boundary and lose
                    // half their glyphs to the wrap's overflow clip — nudge
                    // the text inward while the mark stays on its position.
                    tick.pct < 2 ? styles.tickEdgeStart : '',
                    tick.pct > 98 ? styles.tickEdgeEnd : '',
                  ].filter(Boolean).join(' ')}
                  style={{ left: `${tick.pct}%` }}
                >
                  <div className={styles.tickMark} />
                  {tick.label && <span className={styles.tickLabel}>{tick.label}</span>}
                </div>
              ))}
            </div>
          </div>

        </div>

        {overviewOpen && totalTimeMs && (
          <div className={styles.overviewPanel}>
            <div
              ref={overviewRef}
              className={styles.overviewTrack}
              onPointerDown={handleOverviewPointerDown}
              onPointerMove={handleOverviewPointerMove}
              onPointerUp={finishRangeDrag}
              onPointerCancel={finishRangeDrag}
              onWheel={handleOverviewWheel}
              onDoubleClick={() => updateViewRange(0, totalTimeMs)}
            >
              {/* Muted copy of the activity heat so the whole recording's
                 profile is scannable while picking a section. */}
              {overviewHeatBands.map((band, index) => (
                <div
                  key={`oh${index}`}
                  className={`${styles.heat} ${styles.overviewHeat} ${styles[`heat_${band.kind}`]} ${styles[`heatL${band.level}`]}`}
                  style={{ left: `${band.left}%`, width: `${band.width}%` }}
                />
              ))}

              <div
                className={styles.progressThread}
                style={{ clipPath: `inset(0 ${100 - clamp(fullHeadPct, 0, 100)}% 0 0)` }}
              />

              {overviewClusters.map((cluster, index) => {
                const event = cluster.events[0]!
                const count = cluster.events.length
                const isMulti = count > 1
                return (
                  <button
                    key={index}
                    type="button"
                    className={[
                      styles.overviewEvent,
                      isMulti ? styles.overviewCluster : '',
                      EVENT_CLASS[cluster.primaryType] ?? '',
                    ].filter(Boolean).join(' ')}
                    style={{ left: `${cluster.pct}%` }}
                    title={isMulti ? `${count} событий` : (event.label ?? cluster.primaryType)}
                    aria-label={isMulti ? `${count} событий` : (event.label ?? cluster.primaryType)}
                    onPointerDown={event => event.stopPropagation()}
                    onClick={() => seekToMs(event.time)}
                  >
                    {isMulti ? <span className={styles.overviewCount}>{count}</span> : null}
                  </button>
                )
              })}

              {/* Inverted selection: the picked section keeps full brightness
                 while everything outside falls into shadow. */}
              <div
                className={styles.overviewShade}
                style={{
                  left: 0,
                  width: `${(effectiveRange.start / totalTimeMs) * 100}%`,
                }}
              />
              <div
                className={styles.overviewShade}
                style={{
                  left: `${(effectiveRange.end / totalTimeMs) * 100}%`,
                  right: 0,
                }}
              />

              <div
                className={styles.overviewSelection}
                style={{
                  left: `${(effectiveRange.start / totalTimeMs) * 100}%`,
                  width: `${((effectiveRange.end - effectiveRange.start) / totalTimeMs) * 100}%`,
                }}
                onPointerDown={event => startRangeDrag('move', event)}
              >
                <span
                  className={`${styles.overviewHandle} ${styles.overviewHandleStart}`}
                  onPointerDown={event => startRangeDrag('start', event)}
                />
                <span
                  className={`${styles.overviewHandle} ${styles.overviewHandleEnd}`}
                  onPointerDown={event => startRangeDrag('end', event)}
                />
              </div>

              <div
                className={styles.overviewCurrent}
                style={{ left: `${fullHeadPct}%` }}
              />
            </div>
          </div>
        )}
      </div>}

      {/* Right controls */}
      <div className={styles.rightControls}>
        <div className={styles.rightMain}>
          {!collapsed && (
            <button
              type="button"
              className={`${styles.iconBtn} ${styles.overviewToggle}${overviewOpen ? ` ${styles.active}` : ''}`}
              onClick={() => setOverviewOpen(open => !open)}
              title={overviewOpen ? 'Скрыть обзор' : 'Показать обзор'}
              aria-label={overviewOpen ? 'Скрыть обзор' : 'Показать обзор'}
              aria-pressed={overviewOpen}
            >
              <OverviewIcon size="68%" />
            </button>
          )}
          {/* Speed — a stack of plates, one per speed step, like records on a
             gramophone spindle. The front plate is the active speed; a click
             sends it to the bottom of the stack and the next step surfaces.
             The edges of the next plates peek out above, hinting there is a
             stack to cycle through. */}
          <button
            className={styles.speedBtn}
            onClick={cycleSpeed}
            title="Скорость воспроизведения"
            aria-label={`Скорость ×${speed} — переключить`}
          >
            {SPEED_STEPS.map((step, i) => {
              const activeIdx = Math.max(0, SPEED_STEPS.indexOf(speed))
              const depth = (i - activeIdx + SPEED_STEPS.length) % SPEED_STEPS.length
              const shown = Math.min(depth, 3)
              return (
                <span
                  key={step}
                  className={styles.speedLayer}
                  data-front={depth === 0 ? 'true' : undefined}
                  data-hidden={depth > 2 ? 'true' : undefined}
                  style={{ '--depth': shown } as React.CSSProperties}
                  aria-hidden={depth === 0 ? undefined : 'true'}
                >
                  ×{step}
                </span>
              )
            })}
          </button>
        </div>
        {/* Range zoom — a quieter second row tucked UNDER the overview/speed
           buttons (they used to sit inline and ended up lying on the
           timeline). Ghost gold outlines: secondary tools, not plates. */}
        {!collapsed && overviewOpen && totalTimeMs ? (
          <div className={styles.zoomRow}>
            <button
              type="button"
              className={styles.zoomGhost}
              onClick={() => zoomView(1.45)}
              disabled={visibleDuration >= totalTimeMs - 1}
              title="Отдалить интервал"
              aria-label="Отдалить интервал"
            >
              <span className="pixelart-icons-font-zoom-out" />
            </button>
            <button
              type="button"
              className={styles.zoomGhost}
              onClick={() => zoomView(0.7)}
              disabled={visibleDuration <= minViewRange + 1}
              title="Приблизить интервал"
              aria-label="Приблизить интервал"
            >
              <span className="pixelart-icons-font-zoom-in" />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
